/**
 * TrendChart — Reusable lightweight-charts wrapper for time-series data.
 * Supports single-series and multi-series line charts.
 *
 * NOTE: TDD exception — canvas rendering and visual layout are verified
 * visually via Playwright, not unit tests. Data shaping is tested at the
 * component level.
 */

import { useEffect, useRef } from 'react';
import {
  createChart,
  type IChartApi,
  type ISeriesApi,
  ColorType,
  CrosshairMode,
  LineSeries,
} from 'lightweight-charts';

export interface SingleSeriesPoint {
  time: string;
  value: number;
}

export interface MultiSeriesData {
  label: string;
  color: string;
  data: SingleSeriesPoint[];
}

interface TrendChartProps {
  /** Single-series data. Mutually exclusive with `series`. */
  data?: SingleSeriesPoint[];
  /** Multi-series data. Mutually exclusive with `data`. */
  series?: MultiSeriesData[];
  color?: string;
  height?: number;
}

const CHART_THEME = {
  background: '#111827',
  textColor: '#9CA3AF',
  gridColor: '#1F2937',
};

export function TrendChart({ data, series, color = '#22C55E', height = 180 }: TrendChartProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const seriesRefs = useRef<Array<ISeriesApi<any>>>([]);

  useEffect(() => {
    if (!containerRef.current) return;

    // Clean up
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
      seriesRefs.current = [];
    }

    const hasData = (data && data.length > 0) || (series && series.some((s) => s.data.length > 0));
    if (!hasData) return;

    const chart = createChart(containerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: CHART_THEME.background },
        textColor: CHART_THEME.textColor,
      },
      grid: {
        vertLines: { color: CHART_THEME.gridColor },
        horzLines: { color: CHART_THEME.gridColor },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: '#4B5563', width: 1, style: 2, labelBackgroundColor: '#374151' },
        horzLine: { color: '#4B5563', width: 1, style: 2, labelBackgroundColor: '#374151' },
      },
      rightPriceScale: { borderColor: CHART_THEME.gridColor },
      timeScale: {
        borderColor: CHART_THEME.gridColor,
        timeVisible: true,
        secondsVisible: false,
      },
      width: containerRef.current.clientWidth,
      height,
    });

    chartRef.current = chart;

    if (data && data.length > 0) {
      // Single series
      const lineSeries = chart.addSeries(LineSeries, { color, lineWidth: 2 });
      lineSeries.setData(
        data.map((pt) => ({ time: pt.time, value: pt.value }))
      );
      seriesRefs.current = [lineSeries];
    } else if (series) {
      // Multi-series
      seriesRefs.current = series
        .filter((s) => s.data.length > 0)
        .map((s) => {
          const lineSeries = chart.addSeries(LineSeries, {
            color: s.color,
            lineWidth: 2,
            title: s.label,
          });
          lineSeries.setData(s.data.map((pt) => ({ time: pt.time, value: pt.value })));
          return lineSeries;
        });
    }

    chart.timeScale().fitContent();

    // Handle resize
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (chartRef.current) {
          chartRef.current.applyOptions({ width: entry.contentRect.width });
        }
      }
    });
    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
        seriesRefs.current = [];
      }
    };
  }, [data, series, color, height]);

  const hasData = (data && data.length > 0) || (series && series.some((s) => s.data.length > 0));

  if (!hasData) {
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center bg-gray-900 border border-gray-700 rounded-lg text-gray-500 text-sm"
      >
        No data available
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{ height }}
      className="w-full rounded-lg overflow-hidden"
      data-testid="trend-chart"
    />
  );
}
